<template>
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card">
          <div>
            <div class="login-main">
              <h2 class="text-center mb-3">Driver Profile</h2>

              <div class="text-center mb-3">
                <img
                  v-if="driver.hasProfilePicture"
                  :src="`/api/driver/${props.driverId}/profile-picture`"
                  alt="Driver photo"
                  class="rounded-circle border mb-3"
                  style="width: 100px; height: 100px; object-fit: cover;"
                />

                <div class="fs-4 f-w-700">{{ driver.name }}</div>
                <div class="text-muted" v-if="driver.rating !== null">
                  <i class="fa-solid fa-star text-warning"></i>
                  <span class="ms-1">{{ Number(driver.rating).toFixed(1) }}</span>
                </div>
              </div>

              <!-- Vehicle image (public) -->
              <div v-if="hasVehicleImage" class="d-flex justify-content-center mb-3">
                <div class="vehicle-card">
                  <img :src="`/api/driver/${props.driverId}/vehicle-image`" alt="Vehicle image" class="vehicle-image" @error="hasVehicleImage=false"/>
                </div>
              </div>

              <hr/>

              <div class="row justify-content-center">
                <div class="col-md-8">
                  <h5 class="f-w-600 mb-2 text-center">Vehicle</h5>
                  <ul class="list-unstyled mb-0 text-center">
                    <li v-if="driver.vehicle && driver.vehicle.make"><span class="f-w-600">Make:</span>
                      {{ driver.vehicle.make }}
                    </li>
                    <li v-if="driver.vehicle && driver.vehicle.model"><span class="f-w-600">Model:</span>
                      {{ driver.vehicle.model }}
                    </li>
                    <li v-if="driver.vehicle && driver.vehicle.year"><span class="f-w-600">Year:</span>
                      {{ driver.vehicle.year }}
                    </li>
                    <li v-if="driver.vehicle && driver.vehicle.color"><span class="f-w-600">Color:</span>
                      {{ driver.vehicle.color }}
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="error" class="alert alert-warning mt-3">
                {{ error }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {reactive, ref, onMounted} from 'vue';

// Props
const props = defineProps({
  driverId: {type: String, required: true},
});

// Local state
const driver = reactive({
  name: null,
  hasProfilePicture: null,
  rating: null,
  vehicle: {
    make: null,
    model: null,
    year: null,
    color: null,
  }
});

const loading = ref(false);
const error = ref(null);
const hasVehicleImage = ref(false);

async function checkVehicleImage() {
  try {
    const res = await fetch(`/api/driver/${props.driverId}/vehicle-image`, { method: 'GET', cache: 'no-store' });
    hasVehicleImage.value = res.ok;
  } catch (_) {
    hasVehicleImage.value = false;
  }
}

// Methods
async function fetchDriver() {
  loading.value = true;
  try {
    const res = await fetch(`/api/public/driver/${props.driverId}`);
    if (!res.ok) throw new Error('Failed to load driver profile.');
    const payload = await res.json();
    driver.name = payload.name;
    driver.hasProfilePicture = payload.hasProfilePicture;
    driver.rating = payload.rating ?? driver.rating;
    driver.vehicle.make = payload.vehicleMake ?? driver.vehicle.make
    driver.vehicle.model = payload.vehicleModel ?? driver.vehicle.model
    driver.vehicle.year = payload.vehicleYear ?? driver.vehicle.year
    driver.vehicle.color = payload.vehicleColor ?? driver.vehicle.color

    // Check if a vehicle image is available
    await checkVehicleImage();
  } catch (e) {
    error.value = e.message || 'Unable to load driver profile.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchDriver)
</script>

<style scoped>
.vehicle-card{
  max-width: 360px;
}

.vehicle-image{
  width: 100%;
  height: 180px;
  object-fit: contain;
  display: block;
}
@media (max-width: 575.98px) {
  .vehicle-image{
    height: 140px;
  }
}
</style>
